import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { Box } from '@mui/material';
import SEO from '../components/layout/SEO';
import { getAllHomeData } from '../api/home';
import { getHomeBanners } from '../api/banners';
import {
  buildOrganizationSchema,
  buildWebsiteSchema,
  buildWebPageSchema,
} from '../seo/structuredData';

// HeroCarousel is above-the-fold / LCP — load eagerly
import HeroCarousel from '../components/Home/HeroCarousel';

// All below-fold sections are lazy-loaded so they never block the initial render.
// Each becomes its own JS chunk; the browser fetches them in parallel after
// the hero is painted.
const DefenseSection           = lazy(() => import('../components/Home/DefenseSection'));
const ProductCategoriesSection = lazy(() => import('../components/Home/ProductCategoriesSection'));
const IndustriesSection        = lazy(() => import('../components/Home/IndustriesSection'));
const ApplicationsHeroSection  = lazy(() => import('../components/Home/ApplicationsHeroSection'));
const PartnersSection          = lazy(() => import('../components/Home/PartnersSection'));
const JourneySection           = lazy(() => import('../components/Home/JourneySection'));
const FooterCTA                = lazy(() => import('../components/layout/FooterCTA'));

// Minimal placeholder preserves section height while the lazy chunk loads,
// preventing CLS when the real component swaps in.
const SectionPlaceholder = ({ minHeight = 300 }) => (
  <Box sx={{ minHeight, width: '100%' }} aria-hidden="true" />
);

// ─── Normalization helpers ────────────────────────────────────────────────────

const normalizeDefense = (d) => ({
  heading: d.title,
  body: d.subtitle,
  sections: (d.sections || []).map((section) => ({
    title: section.title,
    description: section.description,
    image: section.image?.path || null,
    imageSrcset: section.image?.srcset || null,
    items: (section.items || []).map((item) => ({
      icon: item.icon?.path || null,
      label: item.label,
    })),
  })),
  stats: (d.stats || []).map((stat) => ({
    value: stat.value,
    suffix: stat.suffix || '',
    line1: stat.line1,
    line2: stat.line2,
  })),
  exploreText: d.exploreText || null,
  exploreLink: d.exploreLink || null,
});

const normalizeProductCategories = (pc) =>
  (pc.categories || []).map((cat) => ({
    id: cat.slug || String(cat.id),
    label: cat.name,
    tabLabel: cat.tabLabel || cat.name,
    // API sends catIcon.path (not logo.path)
    icon: cat.catIcon?.path || cat.logo?.path || null,
    description: cat.description || '',
    subtitle: cat.subtitle || '',
    url: `/categories/${cat.slug}`,
    products: (cat.products || []).map((p) => ({
      id: String(p.id),
      name: p.name,
      description: p.short_description || '',
      image: p.base_image?.path || null,
      imageSrcset: p.base_image?.srcset || null,
      // API sends the real hierarchical page URL; the flat /products/{slug} is
      // only a fallback (older API, or a product with no resolvable category).
      url: p.url || `/products/${p.slug}`,
    })),
  }));

const normalizeIndustries = (ind) =>
  (ind.categories || []).map((cat) => ({
    id: cat.slug || String(cat.id),
    label: cat.name,
    description: cat.description || '',
    image: cat.logo?.path || null,
    imageSrcset: cat.logo?.srcset || null,
    banner: cat.banner || null,
    url: `/categories/${cat.slug || cat.id}`,
    applications: (cat.applications || []).map((a) => ({
      id: a.id || a.slug,
      slug: a.slug || '',
      label: a.title || a.name || '',
      description: a.description || '',
      image: a.featured_image || null,
    })),
  }));

const normalizePartners = (partners) =>
  (partners || []).map((p) => ({
    id: p.id,
    name: p.name,
    slug: p.slug,
    // API sends logo as { filename, path } — flatten to string
    logo: p.logo?.path || null,
    url: null,
  }));

// API shape: journey.bgImage.path, journey.story.{title,body,buttonLabel,buttonLink}
//            journey.stats[].{value,label}, journey.valuesTitle, journey.values[].{title,desc}
const normalizeJourney = (j) => ({
  backgroundImage: j.bgImage?.path || null,
  heading:         j.story?.title      || j.title || '',
  body:            j.story?.body       || j.body  || '',
  ctaLabel:        j.story?.buttonLabel || 'KNOW MORE',
  ctaUrl:          j.story?.buttonLink  || '/about',
  stats:           (j.stats || []).map((s) => ({ value: s.value, label: s.label })),
  valuesTitle:     j.valuesTitle || 'Our Values',
  // API uses .desc; static fallback uses .description
  values:          (j.values || []).map((v) => ({
    title:       v.title,
    description: v.desc || v.description || '',
  })),
});

// ─── HomePage ─────────────────────────────────────────────────────────────────

const HomePage = () => {
  const [homeData, setHomeData] = useState(null);
  // true once the fetch settles (success OR failure) so react-snap never hangs
  const [homeLoaded, setHomeLoaded] = useState(false);
  // Hero banners — fetched independently of homeData (see effect below) so
  // the LCP element never waits on unrelated below-the-fold sections.
  const [heroBanners, setHeroBanners] = useState([]);
  // true once the hero fetch settles (success OR failure) — combined with
  // homeLoaded below so react-snap waits for both independent fetches, not
  // just the big one, before snapshotting.
  const [heroLoaded, setHeroLoaded] = useState(false);
  // Stores the full currently-selected industry object {label, description, banner:{path}}
  // so ApplicationsHeroSection can update both bg image AND panel content on card click
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const applicationsRef = useRef(null);

  useEffect(() => {
    // Standalone, fast hero-carousel fetch — deliberately independent of the
    // combined homeData effect below, so the hero (LCP element) renders as
    // soon as ITS OWN small query resolves instead of waiting on defense
    // section / solutions / journey / etc. Same stale-while-revalidate
    // caching pattern as homeData, so repeat visits still reveal instantly.
    try {
      const cachedBanners = localStorage.getItem('dynalog_hero_banners');
      if (cachedBanners) {
        const parsed = JSON.parse(cachedBanners);
        if (Array.isArray(parsed) && parsed.length) setHeroBanners(parsed);
      }
    } catch (err) {
      console.error('Failed to parse cached hero banners:', err);
    }

    getHomeBanners()
      .then((banners) => {
        if (banners?.length) {
          setHeroBanners(banners);
          try {
            localStorage.setItem('dynalog_hero_banners', JSON.stringify(banners));
          } catch (err) {
            console.error('Failed to cache hero banners:', err);
          }
        }
      })
      .finally(() => {
        setHeroLoaded(true);
      });
  }, []);

  useEffect(() => {
    // 1. Try to load cached homepage data from localStorage first for instant load
    try {
      const cachedData = localStorage.getItem('dynalog_home_data');
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        setHomeData(parsed);
        setHomeLoaded(true);
      }
    } catch (err) {
      console.error('Failed to parse cached home data:', err);
    }

    // 2. Fetch fresh data from the server (stale-while-revalidate)
    getAllHomeData()
      .then((data) => {
        if (data) {
          setHomeData(data);
          try {
            localStorage.setItem('dynalog_home_data', JSON.stringify(data));
          } catch (err) {
            console.error('Failed to cache home data:', err);
          }
        }
      })
      .finally(() => {
        setHomeLoaded(true);
      });
  }, []);

  const pc  = homeData?.productCategories;
  const ind = homeData?.industries;

  // ── CRITICAL: memoize all normalized arrays ──────────────────────────────
  // Without useMemo, every setSelectedIndustry() call re-renders HomePage
  // which re-computes normalizeIndustries() inline → new array reference
  // → IndustriesSection's useEffect fires → resets selectedIndex → 0
  // → overwrites the user's click in <1 second.
  const normalizedIndustries = useMemo(
    () => (ind ? normalizeIndustries(ind) : null),
    [ind]
  );
  const normalizedCategories = useMemo(
    () => (pc ? normalizeProductCategories(pc) : null),
    [pc]
  );
  const normalizedDefense = useMemo(
    () => (homeData?.defenseSection ? normalizeDefense(homeData.defenseSection) : null),
    [homeData?.defenseSection]
  );
  const normalizedPartners = useMemo(
    () => (homeData?.partners ? normalizePartners(homeData.partners) : null),
    [homeData?.partners]
  );
  const normalizedJourney = useMemo(
    () => (homeData?.journey?.enabled !== false && homeData?.journey
      ? normalizeJourney(homeData.journey)
      : null),
    [homeData?.journey]
  );

  return (
    <>
      <SEO
        title="Industrial Computing, Data Acquisition Hardware Supplier In India"
        description="Dynalog stands at the forefront of our country's technological revolution, with a steadfast commitment to excellence and innovation."
        keywords="industrial access points supplier, industrial network switches supplier, marine display monitor, rugged network switch, fanless embedded pc, embedded computing solutions, usb input output module"
        structuredData={[
          buildWebPageSchema({
            title: 'Dynalog India Homepage',
            description: 'Industrial technology partner for reliable edge-to-cloud connectivity and infrastructure.',
            url: '/',
          }),
          buildWebsiteSchema(),
          buildOrganizationSchema(),
        ]}
        pageReady={homeLoaded && heroLoaded}
      />

      {/* 1 — Hero Carousel: eager, it is the LCP element. apiData comes from its
          own fast endpoint (see effect above), not the combined homeData call,
          so it never waits on unrelated below-the-fold sections. */}
      <HeroCarousel apiData={heroBanners} />

      {/* 2-8 — Below-fold sections: lazy chunks, fetched after hero paints */}
      <Suspense fallback={<SectionPlaceholder minHeight={400} />}>
        <DefenseSection apiData={normalizedDefense} />
      </Suspense>

      <Suspense fallback={<SectionPlaceholder minHeight={500} />}>
        <ProductCategoriesSection
          apiData={normalizedCategories}
          sectionTitle={pc?.sectionTitle}
          sectionSubtitle={pc?.sectionSubtitle}
        />
      </Suspense>

      <Suspense fallback={<SectionPlaceholder minHeight={500} />}>
        <IndustriesSection
          apiData={normalizedIndustries}
          sectionTitle={ind?.sectionTitle}
          onIndustryChange={setSelectedIndustry}
          scrollTarget={applicationsRef}
        />
      </Suspense>

      <div ref={applicationsRef}>
      <Suspense fallback={<SectionPlaceholder minHeight={500} />}>
        <ApplicationsHeroSection
          selectedIndustry={selectedIndustry}
          fallbackBgImage={homeData?.applicationsBgImage || null}
        />
      </Suspense>
      </div>

      <Suspense fallback={<SectionPlaceholder minHeight={300} />}>
        <PartnersSection
          apiData={normalizedPartners}
          heading={homeData?.partnersHeading || null}
          subheading={homeData?.partnersSubheading || null}
        />
      </Suspense>

      <Suspense fallback={<SectionPlaceholder minHeight={400} />}>
        <JourneySection apiData={normalizedJourney} />
      </Suspense>

      <Suspense fallback={<SectionPlaceholder minHeight={200} />}>
        <FooterCTA />
      </Suspense>
    </>
  );
};

export default HomePage;
